import React, { forwardRef, useId } from "react";

import { cornerClass } from "./joined";

const cx = (...a) => a.filter(Boolean).join(" ");

// elementul titlului, pe niveluri (titleAs îl schimbă)
const TITLE_TAG = { screen: "h2", panel: "h3", section: "h2", card: "h3", popup: "h3" };

/**
 * CardUI — singura ramă a aplicației: placa unui ecran, panoul din unelte,
 * secțiunea cu contur, cartonașul și interiorul unui popup. Înlocuiește
 * SectionCard, SectionPanel (+ outlined), PagePanel, PopupLayout și candidații
 * PageHeader, SettingsCard, ActionCard, SelectorCard. Stilurile: scss/UI/_cardUI.scss.
 *
 * level: screen (placa unui ecran: black-s, rază 35 superellipse, 12px, titlul
 *           Unbounded fs(32px) — student-practice, today, studentsUI…) |
 *        panel (implicit — panoul din unelte: black-s, rază 20, 12px, coloană la
 *           10, titlul Unbounded 21px — exam-admin__panel) |
 *        section (secțiunea cu contur: 1px --bd, rază 30, 16px, titlul 22px / 500,
 *           descrierea în dreapta titlului pe ecran lat — adminOther__section) |
 *        card (cartonașul black-p, rază 24, 14px, coloană la 12, titlul 16px / 600,
 *           descrierea 13px white-s — messageTemplates__card, cardurile de
 *           categorie / recenzie / videoclip) |
 *        popup (interiorul unui popup: titlul fs(32px), loc în dreapta pentru
 *           butonul de închidere, conținutul care derulează — createReservationPopup)
 * title: titlul · titleAs: elementul lui (implicit h2 la screen / section, h3 în rest,
 *        h1 la titleSize="lg")
 * titleSize: md (implicit) | sm (28px: placa „Azi”, popup-urile mai mici) |
 *        lg (titlul de pagină Unbounded clamp(32px, 5vw, 56px): antetul paginilor de
 *        unelte, adminOther__hero)
 * eyebrow: supratitlul mic cu majuscule (text sau link „Acasă / Jurnal”)
 * description: textul de sub titlu (la section: în dreapta lui pe ecran lat)
 * actions: în dreapta titlului (butoane, insigne, filele, căutarea)
 * toolbar: rândul de sub antet (căutarea lipită de buton din Personal)
 * footer: jos, sub conținut (ButtonUI.Row, starea + butoanele, contorul)
 * scroll: corpul derulează fără bară (scroll-area); rama ia înălțimea de la părinte
 *        (height: 100%). Implicit da la popup.
 * fade: și estomparea de 24px sus (lista din panourile de pagină); implică scroll
 * padding: none | sm (4px la screen, 6px în rest; la card și 6px între părți) |
 *        md (implicit nivelului) | lg (16px; 22px la section: antetul de pagină)
 * fill: cel puțin cât ecranul minus 24px (Examen, Videoclipuri)
 * surface: panel (black-s) | page (black-p) — altă culoare decât a nivelului
 * align: start (implicit) | center (antetul centrat: cardul de alegere „Selectează cutia”)
 * joined: lipit de vecini într-o grilă (rază 8): pus într-un ListUI, cardurile de la
 *        margine își rotunjesc singure colțurile de afară la 24 (corners le pune ListUI;
 *        Categorii și Recenzii)
 * onOpen: conținutul devine zona apăsabilă de sus a cardului (ActionCard);
 *        openLabel îi dă numele pentru cititoarele de ecran
 * as: elementul ramei (implicit section când are titlu, altfel div; div la popup)
 * Fără scroll, conținutul stă direct în ramă (selectorii de tip .student-exam > .card
 * merg mai departe); cu scroll, într-un cardUI__body.
 */
const CardUI = forwardRef(function CardUI(
   {
      as,
      level = "panel",
      title,
      titleAs,
      titleSize = "md",
      eyebrow,
      description,
      actions,
      toolbar,
      footer,
      scroll,
      fade = false,
      padding = "md",
      fill = false,
      surface,
      align = "start",
      joined = false,
      corners,
      gridSpan,
      onOpen,
      openLabel,
      className = "",
      style,
      children,
      ...rest
   },
   ref,
) {
   const titleId = useId();
   const hasTitle = title != null && title !== false;
   const scrolls = fade || (scroll ?? level === "popup");
   const Component = as || (level !== "popup" && hasTitle ? "section" : "div");
   const Title = titleAs || (titleSize === "lg" ? "h1" : TITLE_TAG[level] || "h3");
   const hasHeading = hasTitle || eyebrow != null || description != null;
   // la secțiune titlul și descrierea sunt frați în antet (ca adminOther__sectionHead)
   const inlineHead = level === "section" && titleSize !== "lg" && eyebrow == null;
   // regiunea primește numele din titlu
   const labelled = hasTitle && (Component === "section" || Component === "article");

   const content = onOpen ? (
      <button type="button" className="cardUI__open" onClick={onOpen} aria-label={openLabel}>
         {children}
      </button>
   ) : (
      children
   );

   return (
      <Component
         ref={ref}
         className={cx(
            "cardUI",
            `cardUI--${level}`,
            padding !== "md" && `cardUI--pad-${padding}`,
            titleSize !== "md" && `cardUI--title-${titleSize}`,
            surface && `cardUI--surface-${surface}`,
            align === "center" && "cardUI--center",
            joined && "cardUI--joined",
            scrolls && "cardUI--scroll",
            fill && "cardUI--fill",
            cornerClass(corners),
            className,
         )}
         style={gridSpan ? { gridColumn: `span ${gridSpan}`, ...style } : style}
         aria-labelledby={labelled ? titleId : undefined}
         {...rest}
      >
         {(hasHeading || actions) && (
            <div className={cx("cardUI__head", inlineHead && "cardUI__head--inline", actions && "cardUI__head--actions")}>
               {hasHeading && (
                  <div className="cardUI__heading">
                     {eyebrow != null && <p className="cardUI__eyebrow">{eyebrow}</p>}
                     {hasTitle && (
                        <Title id={titleId} className="cardUI__title">
                           {title}
                        </Title>
                     )}
                     {description != null && <p className="cardUI__description">{description}</p>}
                  </div>
               )}
               {actions && <div className="cardUI__actions">{actions}</div>}
            </div>
         )}
         {toolbar && <div className="cardUI__toolbar">{toolbar}</div>}
         {scrolls ? <div className={cx("cardUI__body", fade && "cardUI__body--fade")}>{content}</div> : content}
         {footer && <div className="cardUI__footer">{footer}</div>}
      </Component>
   );
});

// ListUI îi dă colțurile de la margine (corners) ca rândurilor lui
CardUI.joinedTile = true;

export default CardUI;
